import React, { useState, useEffect, useCallback, useRef } from 'react';
import Shell from './Shell';
import Lecon from '../Lecon';
import { authHeaders, jsonAuthHeaders } from '../apiAuth';

// ── ÉCHELLE DE DÉCLINAISON (page personnelle) ───────────────────────────────
// Mesuré le 2026-07-30 : reconnaître un cas 80 %, en PRODUIRE un 11 %. Un facteur
// sept. Les copies d'écriture disaient la même chose, le nom restant toujours à
// la forme du dictionnaire (« чем москва » pour « чем Москву »).
//
// D'où le parti pris de cet écran, qui n'est pas celui de /?formes :
//   - un CAS à la fois, et on ne passe au suivant qu'une fois celui-ci tenu ;
//   - la classe de déclinaison est l'unité de progrès, affichée en permanence ;
//   - on TAPE la forme, on ne la choisit pas. À 80 % de reconnaissance contre
//     11 % de production, un QCM mesurerait la mauvaise chose ;
//   - le retour donne la RÈGLE de la classe, pas seulement la bonne réponse.
//
// Aucun chrono. Une déclinaison se cherche : mettre la pression sur un geste
// qu'on ne maîtrise pas encore ne fait que réinstaller l'échec.

const CLE_LECON = 'decliner-v1';

// Barre de progression d'une classe : les 5 dernières réponses de la paire
// (cas, classe). C'est exactement la fenêtre sur laquelle le serveur juge qu'une
// terminaison est tenue, donc l'utilisateur voit la règle qui le concerne.
const Fenetre = ({ recents, tenu }) => (
  <span className="inline-flex items-center gap-[3px]" aria-label={tenu ? 'tenu' : 'en cours'}>
    {Array.from({ length: 5 }, (_, i) => {
      const v = recents[i];
      return (
        <span
          key={i}
          className={`h-1.5 w-3 rounded-full ${
            v === undefined ? 'bg-slate-700' : v ? 'bg-green-500/70' : 'bg-red-500/60'
          }`}
        />
      );
    })}
  </span>
);

export default function Decliner({ cursive, toggleCursive }) {
  const [phase, setPhase] = useState('chargement');   // chargement | lecon | quiz | bilan
  const [etat, setEtat] = useState(null);
  const [serie, setSerie] = useState(null);
  const [i, setI] = useState(0);
  const [essai, setEssai] = useState('');
  const [verdict, setVerdict] = useState(null);
  const [score, setScore] = useState({ ok: 0, total: 0 });
  const champRef = useRef(null);

  // `garderPhase` : rafraîchir l'échelle SANS reprendre la main sur l'écran. En
  // fin de série on veut les compteurs à jour derrière le bilan ; sans ce drapeau,
  // la réponse arrivait après coup et renvoyait à l'accueil, donc le bilan
  // n'était jamais visible. Attrapé par Decliner.test.jsx, pas à l'œil.
  const charger = useCallback((garderPhase = false) => {
    fetch('/api/tuteur/decl/etat', { headers: authHeaders() })
      .then((r) => {
        if (r.status === 401 || r.status === 403) { window.location.replace('/'); return null; }
        if (!r.ok) throw new Error('http');
        return r.json();
      })
      .then((d) => { if (d) { setEtat(d); if (!garderPhase) setPhase('lecon'); } })
      .catch(() => { if (!garderPhase) setPhase('erreur'); });
  }, []);

  useEffect(() => { charger(); }, [charger]);

  const lancer = useCallback((slot) => {
    setPhase('chargement');
    const url = slot ? `/api/tuteur/decl/serie?slot=${slot}` : '/api/tuteur/decl/serie';
    fetch(url, { headers: authHeaders() })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http'))))
      .then((d) => {
        setSerie(d);
        setI(0); setEssai(''); setVerdict(null); setScore({ ok: 0, total: 0 });
        setPhase((d.questions || []).length ? 'quiz' : 'vide');
      })
      .catch(() => setPhase('erreur'));
  }, []);

  // Le champ reprend le focus à chaque nouvelle carte : sans ça, il faut viser
  // l'input au doigt entre chaque mot, ce qui casse le rythme d'une série de dix.
  useEffect(() => {
    if (phase === 'quiz' && !verdict) champRef.current?.focus();
  }, [phase, i, verdict]);

  const q = serie?.questions?.[i];

  const repondre = useCallback(() => {
    if (!q || verdict || !essai.trim()) return;
    fetch('/api/tuteur/decl/answer', {
      method: 'POST',
      headers: jsonAuthHeaders(),
      body: JSON.stringify({ word_id: q.word_id, slot: q.slot, essai }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('http'))))
      .then((d) => {
        setVerdict(d);
        setScore((s) => ({ ok: s.ok + (d.ok ? 1 : 0), total: s.total + 1 }));
      })
      .catch(() => setVerdict({ ok: false, attendu: '?', regle: '' }));
  }, [q, essai, verdict]);

  const suivant = useCallback(() => {
    if (i + 1 >= (serie?.questions?.length || 0)) { setPhase('bilan'); charger(true); return; }
    setI((n) => n + 1); setEssai(''); setVerdict(null);
  }, [i, serie, charger]);

  // Entrée valide, puis Entrée enchaîne. Tout se joue au clavier : c'est un
  // exercice de frappe, la souris n'a rien à y faire.
  const surTouche = (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (verdict) suivant(); else repondre();
  };

  const enveloppe = (contenu) => (
    <Shell actif="/?decliner" cursive={cursive} toggleCursive={toggleCursive}>{contenu}</Shell>
  );

  if (phase === 'chargement') {
    return enveloppe(
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-2 border-blue-400 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (phase === 'erreur' || phase === 'vide') {
    return enveloppe(
      <>
        <p className="text-slate-400 pt-10">
          {phase === 'vide' ? 'Aucun mot disponible pour cette étape.' : 'Impossible de charger l\'échelle.'}
        </p>
        <a href="/?tuteur" className="self-start px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors">
          Retour au tuteur
        </a>
      </>
    );
  }

  // ── Accueil : le cours, les DEUX échelles, les boutons ────────────────────
  if (phase === 'lecon' || phase === 'bilan') {
    // `familles` est arrivé avec les verbes. Repli sur l'ancienne forme à plat
    // pour qu'un navigateur gardant l'ancien bundle en cache ne voie pas un
    // écran vide le temps que le nouveau se charge.
    const familles = etat?.familles || {
      noms: { titre: 'Noms', etapes: etat?.etapes || [], courante: etat?.courante,
              franchies: 0, total_etapes: (etat?.etapes || []).length },
    };
    return enveloppe(
      <>
        <div className="flex items-baseline justify-between">
          <h1 className="text-xl md:text-2xl font-bold">Décliner</h1>
          {etat?.total > 0 && (
            <span className="text-xs text-slate-500 tabular-nums">
              {etat.ok} / {etat.total} depuis le début
            </span>
          )}
        </div>

        {phase === 'bilan' && (
          <div className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-4">
            <p className="text-slate-200">
              Série terminée : {score.ok} sur {score.total}.
            </p>
          </div>
        )}

        <Lecon id={CLE_LECON} titre="Comment marche cet exercice">
          <p>
            Tu reconnais un cas 8 fois sur 10. Tu en produis un 1 fois sur 10.
            C'est le seul écart qui compte encore, et il se referme d'une seule
            façon : en écrivant les terminaisons, pas en les reconnaissant.
          </p>
          <p>
            Un nom russe appartient à une <strong>classe de déclinaison</strong>,
            qui se lit sur sa dernière lettre au nominatif. Toute la classe suit la
            même terminaison. Apprendre les six cas de 500 mots est impossible ;
            apprendre 8 classes l'est tout à fait.
          </p>
          <p>
            L'exercice ne te donne <strong>qu'un cas à la fois</strong>. Tu ne verras
            l'instrumental que lorsque l'accusatif tiendra. Une classe est « tenue »
            quand 4 de tes 5 dernières réponses sont justes.
          </p>
          <p>
            Une phrase porteuse déclenche le cas, comme dans la vraie langue :
            « Я вижу ___ » appelle un accusatif sans jamais prononcer le mot
            « accusatif ». Tape la forme, puis Entrée.
          </p>
          <p className="text-slate-400">
            Aucun chrono ici. Une déclinaison se cherche, et se tromper en cherchant
            fait partie du travail.
          </p>
        </Lecon>

        {/* UNE COLONNE PAR ÉCHELLE. Les deux avancent séparément : le passé des
            verbes est presque gratuit (infinitif sans -ть) alors que l'accusatif
            des noms demande de connaître l'animation. Les fondre en une seule
            file aurait bloqué la marche facile derrière la difficile. */}
        {Object.entries(familles).map(([cle, fam]) => {
          const courante = fam.etapes?.find((e) => e.slot === fam.courante);
          if (!courante) return null;
          return (
            <div key={cle} className="flex flex-col gap-2">
              <div className="flex items-baseline justify-between">
                <h2 className="text-sm font-bold uppercase tracking-wider text-slate-400">
                  {fam.titre}
                </h2>
                <span className="text-xs text-slate-600 tabular-nums">
                  {fam.franchies} / {fam.total_etapes} étapes franchies
                </span>
              </div>

              <div className="bg-slate-800 border border-slate-700 rounded-xl px-5 py-4 flex flex-col gap-3">
                <div className="flex items-baseline justify-between gap-3">
                  <h3 className="font-semibold text-blue-300">{courante.nom}</h3>
                  <span className="text-xs text-slate-500 tabular-nums shrink-0">
                    {courante.tenues} / {courante.classes} classes
                  </span>
                </div>
                <p className="text-sm text-slate-400">{courante.quand}</p>
                <p lang="ru" className="text-sm text-slate-300">
                  {courante.porteuse} <span className="text-slate-500">· {courante.porteuse_fr}</span>
                </p>
                <button
                  onClick={() => lancer(courante.slot)}
                  className="self-start px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
                >
                  Dix {cle === 'verbes' ? 'verbes' : 'noms'}
                </button>
              </div>

              {/* L'échelle entière, pour voir où l'on va. Une étape à venir n'est
                  pas cachée : savoir qu'il reste l'impératif fait partie de la carte. */}
              {fam.etapes.map((e) => {
                const active = e.slot === fam.courante;
                return (
                  <div
                    key={e.slot}
                    className={`rounded-xl border px-4 py-3 ${
                      active ? 'border-blue-500/60 bg-slate-800' : 'border-slate-700/50 bg-slate-800/40'
                    }`}
                  >
                    <div className="flex items-center justify-between gap-3">
                      <span className={`text-sm font-medium ${e.finie ? 'text-green-400' : active ? 'text-blue-300' : 'text-slate-400'}`}>
                        {e.finie ? '✓ ' : ''}{e.nom}
                      </span>
                      <span className="text-xs text-slate-500 tabular-nums shrink-0">
                        {e.tenues} / {e.classes}
                      </span>
                    </div>
                    {active && (
                      <div className="mt-3 flex flex-col gap-1.5">
                        {e.detail.map((d) => (
                          <div key={d.modele} className="flex items-center justify-between gap-3 text-xs">
                            <span className={d.tenu ? 'text-slate-500' : 'text-slate-300'}>
                              {d.libelle}
                              <span className="text-slate-600"> · {d.regle}</span>
                            </span>
                            <Fenetre recents={d.recents} tenu={d.tenu} />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          );
        })}
      </>
    );
  }

  // ── L'exercice ────────────────────────────────────────────────────────────
  return enveloppe(
    <>
      <div className="flex items-baseline justify-between">
        <h1 className="text-xl md:text-2xl font-bold">{serie.nom}</h1>
        <span className="text-xs text-slate-500 tabular-nums">
          {i + 1} / {serie.questions.length}
          {score.total > 0 && ` · ${score.ok} juste${score.ok > 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="bg-slate-800 border border-slate-700/50 rounded-2xl px-5 md:px-7 py-6 flex flex-col gap-5 min-h-[22rem]">
        {/* Le mot au nominatif, avec son sens : on ne cherche pas le vocabulaire. */}
        <div className="text-center">
          <p lang="ru" className="text-3xl md:text-4xl font-bold text-blue-300 break-words">
            {q.lemma}
          </p>
          <p className="text-sm text-slate-400 mt-1">{q.sens}</p>
          <p className="text-[11px] text-slate-600 mt-2">{q.libelle}</p>
          {/* L'ASPECT n'est pas un ornement. Un imperfectif à « Он ___ » donne un
              PRÉSENT, un perfectif donne un FUTUR, avec exactement la même
              terminaison. Sans ce rappel, la phrase porteuse serait ambiguë et
              on apprendrait la forme sans savoir ce qu'elle veut dire. */}
          {q.aspect && (
            <p className="text-[11px] mt-1">
              <span className={q.aspect === 'perf' ? 'text-amber-400/80' : 'text-blue-400/80'}>
                {q.aspect === 'perf' ? 'perfectif → futur' : 'imperfectif → présent'}
              </span>
            </p>
          )}
        </div>

        {/* La phrase porteuse : c'est elle qui déclenche le cas. Le trou est là où
            la forme doit aller, pour que la question se lise comme une phrase. */}
        <div className="bg-slate-900/40 border border-slate-700/60 rounded-xl px-4 py-3 text-center">
          <p lang="ru" className="text-lg text-slate-200">
            {serie.porteuse.replace('___', '')}
            <span className="text-blue-400 border-b-2 border-dashed border-blue-500/60 px-6">
              {verdict ? verdict.attendu : ' '}
            </span>
          </p>
          <p className="text-xs text-slate-500 mt-1">{serie.porteuse_fr}</p>
        </div>

        <input
          ref={champRef}
          lang="ru"
          value={essai}
          onChange={(e) => setEssai(e.target.value)}
          onKeyDown={surTouche}
          disabled={!!verdict}
          placeholder="tape la forme"
          autoComplete="off" autoCorrect="off" autoCapitalize="off" spellCheck="false"
          className={`w-full text-center text-xl px-4 py-3 rounded-xl bg-slate-900 border outline-none transition-colors ${
            !verdict ? 'border-slate-600 focus:border-blue-500'
              : verdict.ok ? 'border-green-500/60 text-green-300'
              : 'border-red-500/60 text-red-300'
          }`}
        />

        {/* Le retour. La règle de la classe est le vrai contenu : la bonne forme
            seule ne se transporte pas au mot suivant, la terminaison si. */}
        {verdict ? (
          <div className="flex flex-col gap-2">
            <p className={`text-center font-semibold ${verdict.ok ? 'text-green-400' : 'text-red-400'}`}>
              {verdict.ok ? 'Juste' : verdict.presque ? 'Presque, à une lettre près' : 'Non'}
              {!verdict.ok && (
                <> · <span lang="ru" className="text-slate-200">{verdict.attendu}</span></>
              )}
            </p>
            {verdict.regle && (
              <p className="text-center text-sm text-slate-400">
                {q.libelle} → <span className="text-slate-200">{verdict.regle}</span>
              </p>
            )}
            <div className="flex items-center justify-center gap-2 pt-1">
              <Fenetre recents={verdict.recents || []} tenu={verdict.tenu} />
              {verdict.tenu && <span className="text-xs text-green-500">classe tenue</span>}
            </div>
            <button
              onClick={suivant}
              className="self-center mt-1 px-5 py-2.5 bg-blue-600 hover:bg-blue-500 rounded-lg text-sm font-medium transition-colors"
            >
              {i + 1 >= serie.questions.length ? 'Terminer' : 'Suivant'}
            </button>
          </div>
        ) : (
          <button
            onClick={repondre}
            disabled={!essai.trim()}
            className="self-center px-5 py-2.5 bg-blue-600 hover:bg-blue-500 disabled:bg-slate-700 disabled:text-slate-500 rounded-lg text-sm font-medium transition-colors"
          >
            Valider
          </button>
        )}
      </div>

      <a href="/?tuteur" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">
        Quitter la série
      </a>
    </>
  );
}
